/**
 * GanttToolbar — controls above the Gantt:
 *  - Zoom selector (7d / 15d / 30d / 1y / fit)
 *  - Period filter (start, end)
 *  - Column chooser button (opens ColumnsPanel)
 *  - Critical-only toggle
 *  - Saved views menu
 *  - Compare mode (pick a sync to overlay)
 *  - Export menu (CSV, PNG)
 *  - "Open Multi-Project" button (opens MultiProjectSelector)
 */
import { Calendar, Sliders, Layers, Save, Download, GitCompare, FilterX } from "lucide-react";
import { ZOOM_PRESETS } from "./GanttChart";
import { useI18n } from "../context/I18nContext";

export default function GanttToolbar({
  zoom, onZoomChange,
  period, onPeriodChange,
  criticalOnly, onCriticalOnlyChange,
  onOpenColumns, onOpenViews, onOpenExport, onOpenMultiProject, onOpenCompare,
  onClearFilters, hasColumnFilters = false,
  compareSyncId, taskCount, totalTasks,
  canEdit = true,
  /** Public share view hides Multi-Project (project scope is fixed by the link)
   *  and Compare (would also need cross-project read access). Saved views
   *  stay visible but the menu opens in read-only mode. */
  isPublic = false,
}) {
  const { t } = useI18n();
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-3 flex flex-wrap items-center gap-2" data-testid="gantt-toolbar">
      {/* Zoom */}
      <div className="inline-flex items-center bg-zinc-100 rounded-md p-0.5 mr-2">
        {Object.entries(ZOOM_PRESETS).map(([k, v]) => (
          <button
            key={k}
            onClick={() => onZoomChange?.(k)}
            data-testid={`zoom-${k}`}
            className={`px-2.5 py-1 text-xs font-mono rounded ${zoom === k ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}
          >
            {t(v.labelKey)}
          </button>
        ))}
      </div>

      {/* Period */}
      <div className="inline-flex items-center gap-1 text-xs">
        <Calendar size={13} className="text-zinc-400" />
        <input
          type="date"
          value={period?.start || ""}
          onChange={(e) => onPeriodChange?.({ ...(period || {}), start: e.target.value || null })}
          data-testid="period-start"
          className="px-2 py-1 border border-zinc-200 rounded font-mono text-xs"
        />
        <span className="text-zinc-400">→</span>
        <input
          type="date"
          value={period?.end || ""}
          onChange={(e) => onPeriodChange?.({ ...(period || {}), end: e.target.value || null })}
          data-testid="period-end"
          className="px-2 py-1 border border-zinc-200 rounded font-mono text-xs"
        />
        {(period?.start || period?.end) && (
          <button onClick={() => onPeriodChange?.(null)} className="text-zinc-400 hover:text-zinc-700 text-[11px]" data-testid="period-clear">{t("clear")}</button>
        )}
      </div>

      <div className="flex-1" />

      {/* Toggles + menus */}
      <label className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
        <input type="checkbox" checked={!!criticalOnly} onChange={(e) => onCriticalOnlyChange?.(e.target.checked)} className="rounded text-cs-accent focus:ring-cs-accent" data-testid="critical-only-toggle" />
        {t("critical_path_short")}
      </label>

      <button onClick={onOpenColumns} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50" data-testid="open-columns-btn">
        <Sliders size={12} /> {t("columns")}
      </button>

      {(hasColumnFilters || period?.start || period?.end || criticalOnly) && (
        <button
          onClick={onClearFilters}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-cs-accent text-cs-accent rounded-md hover:bg-cs-accent hover:text-white transition-colors"
          data-testid="clear-filters-btn"
          title={t("clear_filters_title")}
        >
          <FilterX size={12} /> {t("clear_filters")}
        </button>
      )}

      {!isPublic && (
        <button onClick={onOpenMultiProject} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50" data-testid="open-multi-btn">
          <Layers size={12} /> {t("projects")}
        </button>
      )}

      {!isPublic && (
        <button onClick={onOpenCompare} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border rounded-md hover:bg-zinc-50 ${compareSyncId ? "border-cs-accent text-cs-accent" : "border-zinc-200"}`} data-testid="open-compare-btn">
          <GitCompare size={12} /> {compareSyncId ? t("comparing") : t("compare")}
        </button>
      )}

      <button onClick={onOpenViews} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50" data-testid="open-views-btn">
        <Save size={12} /> {t("saved_views_btn")}
      </button>

      <button onClick={onOpenExport} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-zinc-950 text-white rounded-md hover:bg-zinc-800" data-testid="open-export-btn">
        <Download size={12} /> {t("export_btn")}
      </button>

      <div className="text-[11px] font-mono text-zinc-500 ml-2 hidden md:block">
        {taskCount} / {totalTasks}
      </div>
    </div>
  );
}
